import React, { useState, useEffect } from "react";
import { useTenant } from "../../context/TenantContext";
import AddFeatureModal from "./AddFeatureModal"; // Import separate component
import {
  Switch,
  CircularProgress,
  Paper,
  Typography,
  Button,
  Snackbar,
  Alert,
  Box,
  Grid,
  Card,
  CardContent,
  Stack,
  Tooltip,
} from "@mui/material";
import SettingsSuggestIcon from "@mui/icons-material/SettingsSuggest";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";

const GeneralSettings = () => {
  const { features, updateTenantFeatures, loading, refreshFeatures } =
    useTenant();
  const [localFeatures, setLocalFeatures] = useState([]);
  const [saving, setSaving] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [toast, setToast] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (features) setLocalFeatures(features);
  }, [features]);

  const handleToggle = (id) => {
    setLocalFeatures((prev) =>
      prev.map((f) => (f.id === id ? { ...f, status: !f.status } : f)),
    );
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const enabledIds = localFeatures.filter((f) => f.status).map((f) => f.id);
      await updateTenantFeatures(enabledIds);
      setToast({
        open: true,
        message: "Configurations synced across tenant.",
        severity: "success",
      });
    } catch (err) {
      setToast({
        open: true,
        message: "Failed to sync settings.",
        severity: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={10}>
        <CircularProgress />
      </Box>
    );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Box sx={{ margin: "0 auto" }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          justifyContent="space-between"
          alignItems="center"
          spacing={2}
          mb={4}
        >
          <Box>
            <Typography variant="h4" fontWeight="800" letterSpacing="-0.5px">
              System Capabilities
            </Typography>
            <Typography color="textSecondary">
              Enable or disable core modules for the active clinic.
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            onClick={() => setIsModalOpen(true)}
            sx={{
              borderRadius: "12px",
              px: 3,
              py: 1,
              color: "#fff",
              backgroundColor: "#000",
              "&:hover": { backgroundColor: "#333" },
            }}
          >
            New Module
          </Button>
        </Stack>

        {/* Boxes Grid */}
        <Grid container spacing={3}>
          {localFeatures.map((feature) => (
            <Grid item xs={12} sm={6} md={4} key={feature.id}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: "16px",
                  border: "1px solid #e0e0e0",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    borderColor: "primary.main",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.05)",
                  },
                }}
              >
                <CardContent sx={{ p: 3 }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="flex-start"
                  >
                    <Box
                      sx={{
                        bgcolor: "primary.light",
                        p: 1,
                        borderRadius: "15px",
                        color: "#ffffff ",
                        mb: 2,
                      }}
                    >
                      <SettingsSuggestIcon />
                    </Box>
                    <Switch
                      checked={feature.status}
                      onChange={() => handleToggle(feature.id)}
                      color="primary"
                    />
                  </Stack>
                  <Typography variant="h6" fontWeight="700" noWrap>
                    {feature.name}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    sx={{
                      mt: 1,
                      height: "50px",
                      overflow: "hidden",
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                    }}
                  >
                    {feature.description || "No description provided."}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Floating Save Action */}
        <Paper
          elevation={4}
          sx={{
            position: "fixed",
            bottom: 30,
            left: "50%",
            transform: "translateX(-50%)",
            px: 4,
            py: 2,
            borderRadius: "50px",
            display: "flex",
            alignItems: "center",
            gap: 3,
            zIndex: 1000,
            border: "1px solid #eee",
          }}
        >
          <Typography variant="body2" fontWeight="600">
            You have unsaved changes
          </Typography>
          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
            sx={{ borderRadius: "25px", px: 4, color: "#fff" }}
          >
            {saving ? "Updating..." : "Push Changes"}
          </Button>
        </Paper>

        {/* Modal & Feedback */}
        <AddFeatureModal
          open={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={() => {
            refreshFeatures(); 
            setToast({
              open: true,
              message: "Module created and synchronized.",
              severity: "success",
            });
          }}
        />

        <Snackbar
          open={toast.open}
          autoHideDuration={4000}
          onClose={() => setToast({ ...toast, open: false })}
        >
          <Alert severity={toast.severity} variant="filled">
            {toast.message}
          </Alert>
        </Snackbar>
      </Box>
    </DashboardLayout>
  );
};

export default GeneralSettings;
